import { useCallback, useEffect, useState } from "react";
import {
  buildManagerInvitationRequest,
  inviteFarmUser,
  loadManagerInvitations,
  type InvitationRole,
  type ManagerInvitationListItem,
  type ManagerInvitationRequest,
} from "../admin/managerInvitation";
import { supabase } from "../auth/supabaseClient";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface ManagerInvitationPanelProps {
  farmId: string;
  canManageUsers: boolean;
  onCompleted: () => void;
}

function roleLabel(role: InvitationRole): string {
  return role === "manager" ? "مدیر" : "کارگر";
}

function invitationErrorMessage(error: unknown): string {
  const code = error instanceof Error ? error.message : "MANAGER_INVITATION_FAILED";

  if (code.includes("PERMISSION_DENIED")) return "اجازه دعوت کاربر برای این حساب وجود ندارد.";
  if (code.includes("MEMBERSHIP_ALREADY_EXISTS")) {
    return "این ایمیل قبلاً عضو فعال سالن است؛ از بخش ویرایش عضویت استفاده کنید.";
  }
  if (code.includes("INVITATION_ALREADY_PENDING")) {
    return "برای این ایمیل یک دعوت در حال انجام وجود دارد.";
  }
  if (code.includes("AUTH_INVITE_FAILED")) {
    return "ارسال دعوت از سرویس ورود انجام نشد. با همین درخواست دوباره تلاش کنید.";
  }
  if (code.includes("INVITATION_EMAIL_INVALID")) return "ایمیل معتبر نیست.";
  if (code.includes("INVITATION_NAME_INVALID")) return "نام و نام خانوادگی معتبر نیست.";
  if (code.includes("INVITATION_ROLE_INVALID")) return "نقش دعوت معتبر نیست.";

  return code;
}

function jalaliDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

export function ManagerInvitationPanel({
  farmId,
  canManageUsers,
  onCompleted,
}: ManagerInvitationPanelProps) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InvitationRole>("operator");
  const [operationId, setOperationId] = useState("");
  const [pendingRequest, setPendingRequest] = useState<ManagerInvitationRequest>();
  const [invitations, setInvitations] = useState<ManagerInvitationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const loadInvitations = useCallback(async () => {
    if (!canManageUsers) {
      setInvitations([]);
      return;
    }

    try {
      setLoading(true);
      setInvitations(await loadManagerInvitations(supabase, farmId));
    } catch (caught) {
      setError(invitationErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [canManageUsers, farmId]);

  useEffect(() => {
    void loadInvitations();
  }, [loadInvitations]);

  function resetOperation() {
    setOperationId("");
    setPendingRequest(undefined);
    setMessage(undefined);
  }

  function requestConfirmation() {
    setError(undefined);
    setMessage(undefined);

    if (!canManageUsers) {
      setError("اجازه دعوت کاربر برای این حساب وجود ندارد.");
      return;
    }

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setError("دعوت کاربر جدید نیاز به اتصال اینترنت دارد.");
      return;
    }

    try {
      const nextOperationId = operationId || crypto.randomUUID();
      const request = buildManagerInvitationRequest({
        operationId: nextOperationId,
        farmId,
        email,
        fullName,
        role,
      });
      setOperationId(request.operationId);
      setPendingRequest(request);
    } catch (caught) {
      setError(invitationErrorMessage(caught));
    }
  }

  async function confirmInvitation() {
    if (!pendingRequest) return;

    try {
      setBusy(true);
      setError(undefined);

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new Error("INVITATION_REQUIRES_ONLINE");
      }

      const result = await inviteFarmUser(supabase, pendingRequest);
      setMessage(
        result.status === "invited"
          ? "دعوت ارسال شد و عضویت سالن ایجاد شد."
          : result.status === "existing_user_attached"
            ? "کاربر موجود بدون ارسال دعوت تکراری به سالن متصل شد."
            : "دعوت قبلی بازیابی شد و عضویت سالن آماده است.",
      );
      setPendingRequest(undefined);
      setOperationId("");
      setFullName("");
      setEmail("");
      setRole("operator");
      await loadInvitations();
      onCompleted();
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : "";
      setError(
        code === "INVITATION_REQUIRES_ONLINE"
          ? "دعوت کاربر جدید نیاز به اتصال اینترنت دارد."
          : invitationErrorMessage(caught),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!canManageUsers) return null;

  return (
    <article className="manager-card">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">دعوت امن</p>
          <h3>افزودن مدیر یا کارگر جدید</h3>
        </div>
        <span className="count-pill">آنلاین</span>
      </div>

      <p className="section-note">
        مدیر سالن رمز عبور کاربر را تعیین نمی‌کند. لینک دعوت توسط سرویس ورود ارسال می‌شود و کلید
        مدیریتی فقط داخل سرور نگهداری می‌شود.
      </p>

      <div className="operation-field-grid">
        <label>
          نام و نام خانوادگی
          <input
            value={fullName}
            onChange={(event) => {
              setFullName(event.target.value);
              resetOperation();
            }}
          />
        </label>

        <label>
          ایمیل
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              resetOperation();
            }}
          />
        </label>

        <label>
          نقش
          <select
            value={role}
            onChange={(event) => {
              setRole(event.target.value as InvitationRole);
              resetOperation();
            }}
          >
            <option value="operator">کارگر</option>
            <option value="manager">مدیر</option>
          </select>
        </label>
      </div>

      <button
        type="button"
        className="secondary-button"
        disabled={busy}
        onClick={requestConfirmation}
      >
        بررسی و ارسال دعوت
      </button>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      <div className="section-title-row">
        <div>
          <p className="section-kicker">سوابق اخیر</p>
          <h3>دعوت‌های کاربران</h3>
        </div>
        <button
          type="button"
          className="text-button"
          disabled={busy || loading}
          onClick={() => void loadInvitations()}
        >
          بروزرسانی
        </button>
      </div>

      {loading ? <p className="section-note">در حال بارگذاری دعوت‌ها…</p> : null}

      <div className="work-list">
        {invitations.map((invitation) => (
          <div className="work-item pending" key={invitation.operationId}>
            <span className="work-main">
              <strong>
                {invitation.fullName} · {roleLabel(invitation.role)}
              </strong>
              <span>
                {invitation.email} ·{" "}
                {invitation.state === "attached" ? "عضویت ایجاد شده" : "در انتظار تکمیل"} ·{" "}
                {jalaliDate(invitation.createdAt)}
              </span>
            </span>
          </div>
        ))}
        {!loading && invitations.length === 0 ? (
          <p className="empty-state">دعوتی ثبت نشده است.</p>
        ) : null}
      </div>

      {pendingRequest ? (
        <ConfirmationSheet
          title="تأیید دعوت کاربر"
          parts={[
            "دعوت کاربر جدید",
            pendingRequest.fullName,
            pendingRequest.email,
            `نقش: ${roleLabel(pendingRequest.role)}`,
          ]}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => {
            setPendingRequest(undefined);
            setOperationId("");
          }}
          onConfirm={() => void confirmInvitation()}
        />
      ) : null}
    </article>
  );
}
